export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) {
    return 'たった今';
  } else if (minutes < 60) {
    return `${minutes}分前`;
  } else if (hours < 24) {
    return `${hours}時間前`;
  } else if (days < 7) {
    return `${days}日前`;
  } else {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${year}年${month}月${day}日`;
  }
};

// 日本時間の日付文字列を取得（YYYY-MM-DD形式）
export const getJSTDateString = (): string => {
  const now = new Date();
  // JSTはUTC+9
  const jstDate = new Date(now.getTime() + (9 * 60 * 60 * 1000));
  return jstDate.toISOString().split('T')[0];
};

// プレイ時間を時間と分の形式に変換
export const formatPlayTime = (minutes: number): string => {
  if (minutes === 0) {
    return '- 分';
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (hours === 0) {
    return `${remainingMinutes}分`;
  }
  
  if (remainingMinutes === 0) {
    return `${hours}時間`;
  }
  
  return `${hours}時間${remainingMinutes}分`;
};

// 総プレイ時間を小数点1桁の時間形式に変換
export const formatTotalPlayTime = (minutes: number): string => {
  if (minutes === 0) {
    return '-';
  }
  const hours = (minutes / 60).toFixed(1);
  return `${hours}`;
};

export const calculateAgeAtPlay = (birthDate: string, playDate: string): number | null => {
  if (!birthDate || !playDate) return null;
  
  const birth = new Date(birthDate);
  const play = new Date(playDate);
  
  let age = play.getFullYear() - birth.getFullYear();
  const monthDiff = play.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && play.getDate() < birth.getDate())) {
    age--;
  }
  
  return age >= 0 ? age : null;
};

export const calculateYearsAgo = (date: string): number => {
  const playDate = new Date(date);
  const now = new Date();
  
  let years = now.getFullYear() - playDate.getFullYear();
  const monthDiff = now.getMonth() - playDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < playDate.getDate())) {
    years--;
  }
  
  return years;
};

// ライフステージセレクター専用：プレイ時点の年齢ベースの簡易計算
// 注意：これは日本の学校制度とは異なります（4月1日基準ではない）
// ライフステージセレクターでは4月1日の日付のみ扱うため問題ありませんが、
// 任意の日付で学年を表示する場合は calculateSchoolGrade を使用してください
export const getLifeStageFromAgeSimple = (age: number): string => {
  if (age < 6) return `${age}歳`;
  if (age < 12) return `小学${Math.min(Math.max(Math.floor(age - 5), 1), 6)}年生`;
  if (age < 15) return `中学${Math.min(Math.max(Math.floor(age - 11), 1), 3)}年生`;
  if (age < 18) return `高校${Math.min(Math.max(Math.floor(age - 14), 1), 3)}年生`;
  if (age < 22) return `大学${Math.min(Math.max(Math.floor(age - 17), 1), 4)}年生`;
  return `${age}歳`;
};

// 正しい学年計算（日本の学校制度に準拠：4月1日基準）
// 誕生日とプレイ日から、そのプレイ日が属する学年度での正しい学年を計算します
export const calculateSchoolGrade = (birthDate: string, playDate: string): string | null => {
  if (!birthDate || !playDate) return null;
  
  const birth = new Date(birthDate);
  const play = new Date(playDate);
  
  // プレイした年度の4月1日を取得
  const playYear = play.getFullYear();
  let schoolYearStart;
  
  // プレイ日が4月1日より前なら前年度、以降なら当年度
  if (play.getMonth() < 3 || (play.getMonth() === 3 && play.getDate() < 1)) {
    schoolYearStart = new Date(playYear - 1, 3, 1); // 前年度の4月1日
  } else {
    schoolYearStart = new Date(playYear, 3, 1); // 当年度の4月1日
  }
  
  // 4月1日時点での年齢を計算
  let ageAt4_1 = schoolYearStart.getFullYear() - birth.getFullYear();
  const monthDiff = schoolYearStart.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && schoolYearStart.getDate() < birth.getDate())) {
    ageAt4_1--;
  }
  
  // 学年判定（4月1日時点の年齢ベース）
  if (ageAt4_1 < 6) return `${ageAt4_1}歳`;
  if (ageAt4_1 <= 11) return `小学${ageAt4_1 - 5}年生`;
  if (ageAt4_1 <= 14) return `中学${ageAt4_1 - 11}年生`;
  if (ageAt4_1 <= 17) return `高校${ageAt4_1 - 14}年生`;
  if (ageAt4_1 <= 21) return `大学${ageAt4_1 - 17}年生`;
  return `${ageAt4_1}歳`;
}; 